import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Avatar,
  AvatarBadge,
  Badge,
  Box,
  Button,
  CircularProgress,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  Select,
  Text,
  Tooltip,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { useAuth } from "../../../auth-context/auth.context";
import moment from "moment";
import { ClientType } from "../../../types/user";
import { getProspectInfo, updateClient } from "../../../api/user-service";
import UpdateClientModal, { Gender, MaritalStatus } from "../modals/components/UpdateClientModal";

const OnboardingForm = () => {
  const history = useHistory();
  const toast = useToast();
  const { user: cogUser } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<ClientType | undefined>(undefined);

  useEffect(() => {
    fetchClient();
  }, []);

  const fetchClient = () => {
    setIsLoading(true);
    getProspectInfo()
      .then(({ user }) => {
        setUser(user);
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      })
  }

  function isUserOnboarded(user: any): boolean {
    if (!user) return false;

    const requiredFields = [
      'firstName',
      'lastName',
      'dateOfBirth',
      'gender',
      'maritalStatus',
      'addressLine1',
      'city',
      'state',
      'postalCode',
    ];

    // Check if all required fields are truthy (not null, undefined, or empty strings)
    return requiredFields.every(fieldName => user[fieldName] !== undefined && user[fieldName] !== null && user[fieldName] !== '');
  }

  const updateClientInfo = async ({
                                    addressLine1,
                                    dateOfBirth,
                                    city,
                                    companyName,
                                    firstName,
                                    lastName,
                                    management,
                                    middleName,
                                    nickName,
                                    gender,
                                    maritalStatus,
                                    phoneNumber,
                                    postalCode,
                                    state,
                                    userRole,
                                    ownBusiness,
                                    hasBusinessLiabilities,
                                    estimatedBusinessValue,
                                    estimatedDebt,
                                    spouseName,
                                    spouseDob,
                                    note,
                                  }: {
    firstName: string,
    middleName: string,
    lastName: string,
    nickName: string,
    dateOfBirth: string,
    gender: Gender,
    maritalStatus: MaritalStatus,
    phoneNumber: string,
    userRole: string,
    management: string,
    addressLine1: string,
    city: string,
    state: string,
    postalCode: string,
    companyName: string,
    ownBusiness: boolean,
    hasBusinessLiabilities?: boolean,
    estimatedBusinessValue?: string | null,
    estimatedDebt?: string | null,
    spouseName?: string,
    spouseDob?: string | null,
    note?: string,
  }) => {
    try {
      await updateClient(cogUser.userId, [], {
        addressLine1,
        dateOfBirth,
        city,
        fullName: companyName,
        firstName,
        lastName,
        gender,
        maritalStatus,
        management,
        middleName,
        nickName,
        phoneNumber,
        postalCode,
        state,
        accountType: userRole,
        ownBusiness,
        hasBusinessLiabilities,
        estimatedBusinessValue,
        estimatedDebt,
        spouseName,
        spouseDob,
        note,
      })
      toast({
        title: 'Update Prospect',
        description: `${firstName}'s information has been updated.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchClient();
    } catch (error: any) {
      toast({
        title: 'Update Prospect',
        description: error.response?.data.message ?? error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {
        isLoading ? <Flex justify="center" align="center" mt={8}>
            <CircularProgress size={12} isIndeterminate color="green.300" />
          </Flex> :
          <Box>
            <Grid
              gap={4}
              templateRows={{
                base: 'none',
                lg: '1fr',
              }}
              templateColumns={{
                base: 'none',
                lg: 'repeat(4, 1fr)',
              }}>
              <GridItem colSpan={1}>
                <Flex align="center" direction='column' mt="20px">
                  <Tooltip label={user?.receivedInvitations?.[0].accepted ? 'Joined' : 'Not Joined'}
                           placement='right'>
                    <Avatar
                      src={user?.avatarUrl}
                      name={`${user?.firstName} ${user?.lastName}`}
                      size="xl"
                    >
                      <AvatarBadge boxSize='1em'
                                   bg={user?.receivedInvitations?.[0].accepted ? 'green.500' : 'red.500'} />
                    </Avatar>
                  </Tooltip>
                  <Text mt={4} fontWeight="500">Prospect
                    from: {moment(user?.receivedInvitations?.[0].createdAt).format('MMMM DD, YYYY')}
                  </Text>
                  <Badge mt={2} colorScheme={isUserOnboarded(user) ? 'green' : 'red'}
                         fontSize='lg'>Onboarding {isUserOnboarded(user) ? 'Completed' : 'InComplete'}</Badge>
                </Flex>
              </GridItem>
              <GridItem colSpan={{ base: 1, lg: 3 }}>
                <Grid
                  templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                  gap={{ base: 4, md: 8 }}
                >
                  <FormControl>
                    <FormLabel>First Name</FormLabel>
                    <Input readOnly defaultValue={user?.firstName} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Middle Name</FormLabel>
                    <Input readOnly defaultValue={user?.middleName} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Last Name</FormLabel>
                    <Input readOnly defaultValue={user?.lastName} />
                  </FormControl>
                </Grid>
                <Grid mt="16px"
                      templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                      gap={{ base: 4, md: 8 }}
                >
                  <FormControl>
                    <FormLabel>Company Name</FormLabel>
                    <Input readOnly defaultValue={user?.fullName} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Gender</FormLabel>
                    <Select isReadOnly isDisabled defaultValue={user?.gender || 'Unknown'}>
                      <option key={0} value="">
                        Select a gender...
                      </option>
                      <option value="Female">Female</option>
                      <option value="Male">Male</option>
                      <option value="Non-binary">Non-binary</option>
                      <option value="Unknown">Prefer not to say</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Marital Status</FormLabel>
                    <Select isReadOnly isDisabled defaultValue={user?.maritalStatus || 'Unknown'}>
                      <option key={0} value="">
                        Select a status...
                      </option>
                      <option value="Married">Married</option>
                      <option value="Single">Single</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widowed">Widowed</option>
                      <option value="Life Partner">Life Partner</option>
                      <option value="Separated">Separated</option>
                      <option value="Unknown">Prefer not to say</option>
                    </Select>
                  </FormControl>
                </Grid>

                {user?.maritalStatus === 'Married' && (
                  <Grid
                    templateColumns="repeat(2, 1fr)"
                    w="full"
                    gap={4}
                    mt="16px"
                  >
                    <GridItem colSpan={{ base: 2, md: 1 }}>
                      <FormLabel fontWeight="500" color="gray.700">
                        Spouse Name
                      </FormLabel>
                      <Input readOnly defaultValue={user?.spouseName} />
                    </GridItem>
                    <GridItem colSpan={{ base: 2, md: 1 }}>
                      <FormLabel fontWeight="500" color="gray.700">
                        Spouse Dob
                      </FormLabel>
                      <Input readOnly
                             defaultValue={user?.spouseDob ? moment(user?.spouseDob, 'YYYY-MM-DD').format('MM/DD/YYYY') : ''} />
                    </GridItem>
                  </Grid>
                )}

                <Grid mt="16px"
                      templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                      gap={{ base: 4, md: 8 }}
                >
                  <FormControl>
                    <FormLabel>Date of Birth</FormLabel>
                    <Input readOnly
                           defaultValue={user?.dateOfBirth ? moment(user?.dateOfBirth, 'YYYY-MM-DD').format('MM/DD/YYYY') : ''} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input readOnly defaultValue={user?.email} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Phone Number</FormLabel>
                    <Input readOnly defaultValue={user?.phoneNumber} />
                  </FormControl>
                </Grid>
                <Grid mt="16px"
                      templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                      gap={{ base: 4, md: 8 }}
                >
                  <GridItem colSpan={{ base: 1, md: 2 }}>
                    <FormControl>
                      <FormLabel>Address</FormLabel>
                      <Input readOnly defaultValue={user?.addressLine1} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>City</FormLabel>
                      <Input readOnly defaultValue={user?.city} />
                    </FormControl>
                  </GridItem>
                </Grid>
                <Grid mt="16px"
                      templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                      gap={{ base: 4, md: 8 }}
                >
                  <FormControl>
                    <FormLabel>State</FormLabel>
                    <Input readOnly defaultValue={user?.state} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Zip Code</FormLabel>
                    <Input readOnly defaultValue={user?.postalCode} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Country</FormLabel>
                    <Input readOnly defaultValue={user?.country} />
                  </FormControl>
                </Grid>

                <HStack mt={4} justify="start" w="100%">
                  <Text fontWeight="600" fontSize="lg">
                    Do you own a business?
                  </Text>
                  <Badge colorScheme={user?.ownBusiness ? 'green' : 'red'}>{user?.ownBusiness ? 'Yes' : 'No'}</Badge>
                </HStack>

                {user?.ownBusiness && (
                  <Box w="full">
                    <HStack mt={2} justify="start" w="full">
                      <Text fontWeight="500" fontSize="md">
                        Do You Have Any Liabilities / Debt?
                      </Text>
                      <Badge
                        colorScheme={user?.hasBusinessLiabilities ? 'green' : 'red'}>{user?.hasBusinessLiabilities ? 'Yes' : 'No'}</Badge>
                    </HStack>
                    <Grid
                      templateColumns="repeat(2, 1fr)"
                      w="full"
                      gap={4}
                      mt="4px"
                    >
                      {user?.hasBusinessLiabilities && (
                        <GridItem colSpan={{ base: 2, md: 1 }}>
                          <FormLabel
                            fontSize="md"
                            fontWeight="500"
                            color="gray.700"
                          >
                            Estimated total debt/liabilities (optional)
                          </FormLabel>

                          <InputGroup>
                            <InputLeftAddon>$</InputLeftAddon>
                            <Input readOnly defaultValue={user?.estimatedDebt} />
                          </InputGroup>
                        </GridItem>
                      )}

                      <GridItem colSpan={{ base: 2, md: 1 }}>
                        <FormLabel
                          fontSize="md"
                          fontWeight="500"
                          color="gray.700"
                        >
                          Estimated Business Value (optional)
                        </FormLabel>

                        <InputGroup>
                          <InputLeftAddon>$</InputLeftAddon>
                          <Input readOnly defaultValue={user?.estimatedBusinessValue} />
                        </InputGroup>
                      </GridItem>
                    </Grid>
                  </Box>
                )}

                <FormControl mt='16px'>
                  <FormLabel>Note</FormLabel>
                  <Text color='gray.600'>{user?.note}</Text>
                </FormControl>

                <Flex justify="space-between" w='full' mt={4}>
                  <Button colorScheme="brand" variant="outline" onClick={() => history.goBack()}>
                    Back
                  </Button>
                  <Button
                    colorScheme="brand"
                    variant="solid"
                    onClick={onOpen}
                    disabled={cogUser.inviteType === 'observer'}
                  >
                    Update
                  </Button>
                </Flex>
              </GridItem>
            </Grid>
          </Box>
      }
      {
        user && <UpdateClientModal isOpen={isOpen} onClose={onClose} onSave={updateClientInfo} user={user} />
      }
    </Box>
  )
    ;
};

export default OnboardingForm;
